<template>
<v-container fluid>
 

    <v-card>
  
  
    <v-card-title class="blue-grey-lighten-4">
      <span class="text-h6">Chart of Accounts</span>
    </v-card-title>

      <v-card-text class="pt-4">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading accounts...</p>
        </div>

        <div v-else-if="error" class="text-center py-8 text-error">
          <v-icon color="error" size="48">mdi-alert-circle</v-icon>
          <p class="mt-2">{{ error }}</p>
        </div>

        <div v-else id="jstree" class="account-tree"></div>
      </v-card-text>
    </v-card>
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <span class="text-h6">{{ modalTitle }}</span>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="accountForm">
            <!-- Account Type -->
            <v-select v-model="formData.account_type" label="Account Type *" :items="accountTypes"
              :rules="[v => !!v || 'Account type is required']" outlined dense :disabled="isEditMode"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-select>

            <!-- Account Name -->
            <v-text-field v-model="formData.name" label="Account Name *"
              :rules="[v => !!v || 'Account name is required']" outlined dense></v-text-field>

            <!-- Account Code - NOT REQUIRED -->
            <v-text-field v-model="formData.code" label="Account Code" outlined dense type="number"></v-text-field>

            <!-- Opening Balance - Only for LEDGER -->
            <v-text-field  v-if="formData.account_type === 'LEDGER1'" v-model="formData.opening_balance"
              label="Opening Balance" outlined dense type="number" step="0.01" placeholder="0.00"></v-text-field>

            <!-- Payment Method -->
            <v-select v-model="formData.payment_method" label="Payment Method" :items="paymentMethods" outlined
              dense
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="bg-red" text @click="closeDialog">Cancel</v-btn>
          <v-btn class="bg-primary" @click="saveAccount">
            {{ isEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</v-container>
</template>

<script>
import $ from 'jquery';
import 'jstree/dist/jstree.min.js';
import 'jstree/dist/themes/default/style.min.css';
import { toast } from "vue3-toastify";

export default {
  name: 'ChartOfAccounts',

  data() {
    return {
      loading: true,
      error: null,
      dialog: false,
      isEditMode: false,
      selectedNode: null,
      formData: {
        id: null,
        name: '',
        code: '',
        opening_balance: 0,
        account_type: 'GROUP',
        payment_method: 'NONE',
        report_type: 'BALANCE_SHEET',
        parent_acc_account_head_id: null,
        acc_account_head_id: null,
      },
      accountTypes: ['GROUP', 'LEDGER'],
      paymentMethods: ['NONE', 'CASH', 'BANK', 'MOBILE_BANKING'],
      reportTypes: ['BALANCE_SHEET', 'INCOME_STATEMENT'],
    };
  },

  computed: {
    accountHeadList() {
      return this.$store.getters.getAccountHeadList || [];
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Account' : 'Create New Account';
    },
  },

  async mounted() {
    setTimeout(async () => {
      await this.loadAccountData();
    }, 1000)

  },

  beforeUnmount() {
    this.destroyTree();
  },

  methods: {
    async loadAccountData() {
      try {
        this.loading = true;
        this.error = null;

        // Fetch data
        await this.$store.dispatch('getAccountHeadList');

        // Validate data
        if (!this.accountHeadList || this.accountHeadList.length === 0) {
          this.error = 'No account data available';
          this.loading = false;
          return;
        }

        console.log('✅ Data loaded:', this.accountHeadList.length, 'accounts');

        // Set loading to false FIRST
        this.loading = false;

        // Wait for DOM to render the #jstree div
        await this.$nextTick();

        // Small additional delay to ensure DOM is ready
        setTimeout(() => {
          console.log('🌲 Initializing tree...');
          this.initializeTree();
        }, 50);

      } catch (error) {
        console.error('❌ Error:', error);
        this.error = `Failed to load accounts: ${error.message}`;
        this.loading = false;
      }
    },

    transformToJsTreeFormat(nodes, parent = '#') {
      const result = [];

      nodes.forEach(node => {
        const jsTreeNode = {
          id: node.id.toString(),
          parent: parent,
          text: node.text || `${node.name} (${node.code})`,
          state: {
            opened: parent === '#',
          },
          data: {
            id: node.id,
            code: node.code,
            name: node.name,
            account_type: node.account_type,
            payment_method: node.payment_method,
            report_type: node.report_type,
            acc_account_head_id: node.acc_account_head_id,
            parent_acc_account_head_id: node.parent_acc_account_head_id,
            opening_balance: node.opening_balance || 0,
          },
          icon: this.getNodeIcon(node),
          // icon: node.account_type === 'LEDGER' 
          //   ? 'jstree-file' 
          //   : 'jstree-folder',
          a_attr: {
            'data-account-type': node.account_type,
          },
        };

        result.push(jsTreeNode);

        if (node.children && node.children.length > 0) {
          const childNodes = this.transformToJsTreeFormat(
            node.children,
            node.id.toString()
          );
          result.push(...childNodes);
        }
      });

      return result;
    },

    // 🎨 Get icon based on account type and report type
    getNodeIcon(node) {
      if (node.account_type === 'LEDGER') {
        // Different icons for different payment methods
        switch (node.payment_method) {
          case 'CASH':
            return 'mdi mdi-cash';
          case 'BANK':
            return 'mdi mdi-bank';
          case 'CARD':
            return 'mdi mdi-credit-card';
          default:
            return 'mdi mdi-file-document-outline';
        }
      } else {
        // GROUP accounts - different icons by report type
        switch (node.report_type) {
          case 'BALANCE_SHEET':
            return 'mdi mdi-folder-account';
          case 'INCOME_STATEMENT':
            return 'mdi mdi-folder-chart';
          default:
            return 'mdi mdi-folder-open';
        }
      }
    },

    initializeTree() {
      // Check if element exists in DOM
      const $tree = $('#jstree');
      if ($tree.length === 0) {
        console.error('❌ #jstree element not found in DOM!');
        return;
      }

      console.log('✅ #jstree element found, initializing...');

      // Destroy existing tree
      this.destroyTree();

      // Transform data
      const treeData = this.transformToJsTreeFormat(this.accountHeadList);
      console.log('🌲 Initializing tree with data:', treeData.length, 'nodes');

      const self = this;

      // Initialize jsTree
      $tree.jstree({
        core: {
          data: treeData,
          check_callback: true,
          themes: {
            responsive: true,
            stripes: true,
            dots: true,
            icons: true,
          },
        },
        plugins: ['wholerow', 'types', 'contextmenu'],
        types: {
          default: {
            icon: 'mdi mdi-folder',
          },
          file: {
            icon: 'mdi mdi-file-edit-outline',
          },
        },
        contextmenu: {
          items: function (node) {
            const accountType = node.data.account_type;

            // 🔥 GROUP: Show Create + Edit
            if (accountType === 'GROUP') {
              return {
                create: {
                  label: 'Create Child Account',
                  icon: 'jstree-icon jstree-themeicon fa fa-plus',
                  action: function () {
                    self.openCreateDialog(node);
                  },
                },
                edit: {
                  label: 'Edit Account',
                  icon: 'jstree-icon jstree-themeicon fa fa-edit',
                  action: function () {
                    self.openEditDialog(node);
                  },
                },
              };
            }

            // 🔥 LEDGER: Show Edit only
            if (accountType === 'LEDGER') {
              return {
                edit: {
                  label: 'Edit Account',
                  icon: 'jstree-icon jstree-themeicon fa fa-edit',
                  action: function () {
                    self.openEditDialog(node);
                  },
                },
              };
            }

            // Default: no menu
            return {};
          },
        },
      });

      // Event listeners
      $tree.on('ready.jstree', function () {
        console.log('✅ jsTree is ready!');
      });

      $tree.on('select_node.jstree', function (e, data) {
        console.log('📌 Selected:', data.node.text, '| Type:', data.node.data.account_type);
      });
    },

    // open modal to create new 
    openCreateDialog(node) {
      console.log('➕ Create child for:', node.text);

      this.isEditMode = false;
      this.selectedNode = node;

      // Reset form with parent reference
      this.formData = {
        id: null,
        name: '',
        code: '',
        opening_balance: 0,
        account_type: 'GROUP',
        payment_method: 'NONE',
        report_type: node.data.report_type || 'BALANCE_SHEET',
        parent_acc_account_head_id: node.data.id,
        acc_account_head_id: null,
      };

      this.dialog = true;
    },

    // open modal to edit existing
    openEditDialog(node) {
      console.log('✏️ Edit:', node.text);

      this.isEditMode = true;
      this.selectedNode = node;

      // Prefill form with node data
      this.formData = {
        id: node.data.id,
        name: node.data.name,
        code: node.data.code,
        opening_balance: node.data.opening_balance || 0,
        account_type: node.data.account_type,
        payment_method: node.data.payment_method || 'NONE',
        report_type: node.data.report_type || 'BALANCE_SHEET',
        parent_acc_account_head_id: node.data.acc_account_head_id,
        acc_account_head_id: node.data.id,
      };

      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.isEditMode = false;
      this.selectedNode = null;
      this.$refs.accountForm?.reset();

      // Reset formData to default
      this.formData = {
        id: null,
        name: '',
        code: '',
        opening_balance: 0,
        account_type: 'GROUP',
        payment_method: 'NONE',
        report_type: 'BALANCE_SHEET',
        parent_acc_account_head_id: null,
        acc_account_head_id: null,
      };
    },

    async saveAccount() {
      // Validate form
      if (!this.$refs.accountForm.validate()) {
        return;
      }

      try {
        console.log('💾 Saving:', this.formData);

        // Prepare payload (only send opening_balance if LEDGER)
        const payload = { ...this.formData };

        if (payload.account_type !== 'LEDGER') {
          // Remove opening_balance for GROUP accounts
          delete payload.opening_balance;
        }

        if (this.isEditMode) {
          // Update existing account
          await this.$store.dispatch('saveUpdateAccountHead', payload);
          toast.success('Account updated successfully');
        } else {
          // Create new account
          await this.$store.dispatch('saveUpdateAccountHead', payload);
          toast.success('Account created successfully');
        }

        this.closeDialog();

        // Reload tree data
        await this.$store.dispatch('getAccountHeadList');

        // Reinitialize tree
        this.destroyTree();

        await this.$nextTick();

        setTimeout(() => {
          this.initializeTree();
        }, 50);



      } catch (error) {
        console.error('❌ Save error:', error);
        this.$toast.error('Failed to save account');
      }
    },

    destroyTree() {
      const $tree = $('#jstree');
      if ($tree.length && $tree.jstree(true)) {
        $tree.jstree('destroy');
      }
    },
  },
};
</script>

<style scoped>
.account-tree {
  background: #fff;
  border: 1px solid #e0e0e0;
  padding: 1rem;
  border-radius: 8px;
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
}

:deep(.jstree-default .jstree-anchor) {
padding: 0;
    font-size: 17px;
    line-height: 19px;
}

:deep(.jstree-default .jstree-node) {
  margin-top: 2px;
}

:deep(.jstree-default .jstree-clicked) {
  background-color: #1976d2 !important;
  color: white !important;
}

:deep(.jstree-default .jstree-hovered) {
  background-color: #e3f2fd;
}

/* Context menu styling */
:deep(.vakata-context) {
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

:deep(.vakata-context li a) {
  padding: 8px 12px;
  font-size: 14px;
}

:deep(.vakata-context li a:hover) {
  background-color: #e3f2fd;
  color: #1976d2;
}

.jstree-default>.jstree-no-dots .jstree-node, .jstree-default>.jstree-no-dots .jstree-leaf>.jstree-ocl {
    background: none !important;
}
.jstree-default .jstree-node, .jstree-default .jstree-icon {
    background-image: url("/node_modules/jstree/dist/themes/default/32px.png") !important;
}
.jstree-default .jstree-wholerow-clicked {
    background: none;
}

</style>